import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  Download,
  Eye,
  Briefcase,
  Edit,
  Send,
  CheckCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgreementsModule = () => {
  const { consortiumId } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({});
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("basic");

  const sidebarItems = [
    {
      section: "Consortium",
      items: [
        { name: "Overview", path: `/consortium/${consortiumId}`, icon: LayoutDashboard },
        { name: "Agreements", path: `/consortium/${consortiumId}/agreements`, icon: FileText },
        { name: "Meetings", path: `/consortium/${consortiumId}/meetings`, icon: Calendar },
        { name: "Voting", path: `/consortium/${consortiumId}/voting`, icon: Vote },
        { name: "RFPs & Bids", path: `/consortium/${consortiumId}/rfps`, icon: FileSpreadsheet },
        { name: "Consent", path: `/consortium/${consortiumId}/consent`, icon: CheckSquare },
      ],
    },
  ];

  useEffect(() => {
    fetchData();
  }, [consortiumId]);

  const fetchData = async () => {
    try {
      const [templatesRes, agreementsRes] = await Promise.all([
        axios.get(`${API}/agreements/templates`),
        axios.get(`${API}/agreements?consortium_id=${consortiumId}`),
      ]);
      setTemplates(templatesRes.data);
      setAgreements(agreementsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgreement = async () => {
    try {
      const template = templates[selectedType];
      await axios.post(`${API}/agreements`, {
        consortium_id: consortiumId,
        agreement_type: selectedType,
        title: template.title,
        content: template.content,
        terms: template.terms,
      });
      toast.success("Agreement created successfully!");
      setCreateOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create agreement");
    }
  };

  const handleRequestLawyer = async (agreementId) => {
    try {
      await axios.post(`${API}/agreements/${agreementId}/request-lawyer`);
      toast.success("Lawyer review requested!");
      fetchData();
    } catch (error) {
      toast.error("Failed to request lawyer review");
    }
  };

  const handleSign = async (agreementId) => {
    try {
      await axios.post(`${API}/agreements/${agreementId}/sign`);
      toast.success("Agreement signed successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to sign agreement");
    }
  };

  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <div className="space-y-6" data-testid="agreements-module">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Agreement Formats
            </h1>
            <p className="text-slate-600">
              Select and customize consortium agreements
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-agreement-btn">
                <FileText className="w-4 h-4 mr-2" />
                Create Agreement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Agreement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Agreement Type
                  </label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic Consortium Agreement</SelectItem>
                      <SelectItem value="revenue_share">Revenue Share Model</SelectItem>
                      <SelectItem value="area_share">Area Share Model</SelectItem>
                      <SelectItem value="hybrid">Hybrid Model</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {templates[selectedType] && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">{templates[selectedType].title}</h4>
                    <pre className="text-sm text-slate-600 whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {templates[selectedType].content}
                    </pre>
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="btn-primary" onClick={handleCreateAgreement}>
                    Create Agreement
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="templates">
          <TabsList>
            <TabsTrigger value="templates">Templates Library</TabsTrigger>
            <TabsTrigger value="agreements">My Agreements</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(templates).map(([key, template]) => (
                <Card key={key} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#4E80FF]" />
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {key.replace("_", " ")}
                      </Badge>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                      {template.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                      {template.content.substring(0, 150)}...
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setPreviewOpen(true);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="agreements" className="mt-6">
            {agreements.length > 0 ? (
              <div className="space-y-4">
                {agreements.map((agreement) => (
                  <Card key={agreement.id} className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-[#4E80FF]" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-slate-900">
                              {agreement.title}
                            </h3>
                            <p className="text-sm text-slate-500">
                              Version {agreement.version} • Created{" "}
                              {new Date(agreement.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              agreement.status === "finalized"
                                ? "default"
                                : "outline"
                            }
                            className={
                              agreement.status === "finalized" ? "bg-[#4E80FF]" : ""
                            }
                          >
                            {agreement.status}
                          </Badge>
                          {agreement.lawyer_review_requested && (
                            <Badge variant="secondary">
                              <Briefcase className="w-3 h-3 mr-1" />
                              Lawyer Review
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {!agreement.lawyer_review_requested && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRequestLawyer(agreement.id)}
                          >
                            <Briefcase className="w-4 h-4 mr-1" />
                            Book Lawyer
                          </Button>
                        )}
                        {agreement.status !== "signed" && (
                          <Button
                            size="sm"
                            className="btn-primary"
                            onClick={() => handleSign(agreement.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Sign
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    No Agreements Yet
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Create your first agreement from our template library.
                  </p>
                  <Button className="btn-primary" onClick={() => setCreateOpen(true)}>
                    Create Agreement
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.title}</DialogTitle>
            </DialogHeader>
            <div className="bg-slate-50 rounded-lg p-6">
              <pre className="whitespace-pre-wrap text-sm text-slate-700">
                {selectedTemplate?.content}
              </pre>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
              <Button className="btn-primary">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AgreementsModule;
